/**
 * <List> — a titled section skeleton (molecule, L0.1).
 *
 * The shared shape of every parent-home section: an optional title, then either the items
 * (`children`) or an empty-state message, then an optional `footer` slot (the section's CTA
 * button(s)) rendered in BOTH states. Domain sections (`UpcomingSessionsSection`,
 * `UpcomingAssessmentsSection`, …) feed it typed items + a footer instead of re-implementing
 * this title/empty/footer scaffold each time.
 *
 * Le titre est un **libellé** (`<SectionLabel>`, petites capitales grises) par défaut : sur une
 * page parent, le gros texte appartient à l'information, pas au nom du bloc qui la contient.
 * `titleVariant="title"` rend l'ancien `<Title>` — c'est ce que prend un en-tête de JOUR au
 * milieu d'une liste, qui n'est pas un intitulé de section.
 *
 * Pure composition of DS atoms — `<Stack>`, `<SectionLabel>`, `<Title>`, `<Text>`. No hex
 * literals, no domain imports.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { FontSizeKey } from "../../theme"

export type ListProps = {
    /** Section heading — omitted renders no title. */
    title?: string
    /** Glyphe marine devant le titre — rien par défaut (les sections de l'accueil n'en ont pas). */
    titleIcon?: IconName
    /** Fente de fin de l'en-tête — le bouton « en savoir plus » d'une section de formulaire. */
    headerRight?: React.ReactNode
    /** Message shown in place of the items when the list is empty. */
    emptyMessage?: string
    /** Override the auto-detect (default: `React.Children.count(children) === 0`). */
    isEmpty?: boolean
    /** `"label"` (défaut) = petites capitales grises ; `"title"` = le gros titre marine. */
    titleVariant?: "label" | "title"
    /** Title size — only read by `titleVariant="title"`; defaults to `"text-md"`. */
    titleSize?: FontSizeKey
    /** Rendered below the items in BOTH states — the section's CTA button(s). */
    footer?: React.ReactNode
    /** Vertical gap between title / items / footer — spacing token, default `"3"`. */
    gap?: SpacingKey
    children?: React.ReactNode
    testID?: string
}

export function List(props: ListProps) {
    const {
        title,
        titleIcon,
        headerRight,
        titleVariant = "label",
        titleSize,
        emptyMessage,
        isEmpty,
        footer,
        gap = "3",
        children,
        testID,
    } = props
    const empty = isEmpty ?? React.Children.count(children) === 0

    const heading =
        title == null ? null : titleVariant === "title" ? (
            <Title size={titleSize}>{title}</Title>
        ) : (
            <SectionLabel>{title}</SectionLabel>
        )

    return (
        <Stack gap={gap} testID={testID}>
            {/* Sans glyphe ni fente de fin, l'en-tête reste le titre nu : rendu inchangé. */}
            {titleIcon == null && headerRight == null
                ? heading
                : (heading != null || headerRight != null) && (
                      <Row gap="2" justify="between">
                          <Row gap="2" style={styles.heading}>
                              {titleIcon != null && <Icon name={titleIcon} size={16} color="primary" />}
                              {heading}
                          </Row>
                          {headerRight}
                      </Row>
                  )}
            {empty ? emptyMessage != null && <Text color="text-muted">{emptyMessage}</Text> : children}
            {footer}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** Le titre se replie, le glyphe et la fente de fin gardent leur place. */
    heading: { flexShrink: 1 },
}))
